import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import {
    BoxRef,
    boxesParam,
    getAvailableGateways,
} from 'services/easyPay/gateways'
import {
    IGateway,
    getActiveGateways,
    sortGateways,
} from './useIsDefaultGateway'

interface Options {
    /** the `init_pay` payload, used only when the box-aware call is unavailable */
    fallback?: { gateways?: IGateway[] }
    enabled?: boolean
}

/**
 * The gateways to offer for a payment, and the one to start selected.
 *
 * Every pay screen used to read `init_pay`'s `gateways` key, which answers
 * "which gateways exist", not "which gateways will take THIS payment". Both
 * ways of reading it broke, and one member hit both inside two minutes
 * (PostHog `019fd89f…`, fund 34963, 135 clicks):
 *
 *   - the payables screen asks without naming a fund, so the backend takes its
 *     "box unknown → list everything" branch and offers Pasargad to a fund it
 *     is not live on → 400 «درگاه برای این صندوق فعال نیست» on pay;
 *   - the loan screen asks with the fund, so the backend filters correctly and
 *     returns کارت‌به‌کارت alone → which the picker then dropped for having
 *     `is_in_mobile_visible=false` → no gateways on screen at all.
 *
 * So we ask the box-aware endpoint instead. If it is not there yet (an older
 * backend), we fall back to the init payload — which is no worse than what
 * every screen did before, and کارت‌به‌کارت now survives the filter either way.
 */
export default function useAvailableGateways(
    boxes: BoxRef[],
    { fallback, enabled = true }: Options = {}
) {
    const key = boxesParam(boxes)
    const { data, isLoading, isError } = useQuery(
        ['available-gateways', key],
        () => getAvailableGateways(boxes),
        {
            enabled,
            // The allowlist changes when support onboards a fund, i.e. never
            // within a session. Re-asking on every tab focus buys nothing.
            staleTime: 5 * 60_000,
            retry: 1,
        }
    )

    const gateways = data
        ? sortGateways(
              data.filter(
                  (g) =>
                      g.terminal_type === 'htrm' &&
                      g.is_active &&
                      (g.is_in_mobile_visible || g.unique_code === 'HAM_IPG')
              )
          )
        : getActiveGateways(fallback)

    const [selectedCode, setSelectedCode] = useState<string | null>(null)

    useEffect(() => {
        // Only ever *seed* the selection. Re-running on every render of the
        // list would silently move the user's choice back under them when the
        // fallback resolves a beat after the endpoint.
        setSelectedCode((current) =>
            current && gateways.some((g) => g.unique_code === current)
                ? current
                : gateways[0]?.unique_code ?? null
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gateways.map((g) => g.unique_code).join(',')])

    return {
        gateways,
        selectedCode,
        setSelectedCode,
        isLoading: isLoading && !fallback,
        isError,
        /** nothing can take this payment online — show the manual fallback */
        hasNoGateway: !isLoading && gateways.length === 0,
    }
}
